// path: frontend/src/components/LoginScreen.jsx
/**
 * Экран логина / смены / установки пароля. UX-валидация домена e-mail
 * берётся из meta.allowedEmailDomains (GET /api/v1/meta): без meta
 * проверка домена пропускается — окончательная проверка на сервере
 * (REGISTRATION_ERRORS.emailDomainNotAllowed). Так мы не блокируем вход,
 * даже если meta ещё не загрузилась.
 *
 * Остальные проверки (длина/сложность пароля) — UX-зеркало
 * domain/password-policy.ts; сервер всё равно проверяет.
 */
import { useEffect, useRef, useState } from "react";
import { useAllowedEmailDomains } from "../hooks/useMetaHelpers";
import { usePasswordReveal } from "../hooks/usePasswordReveal";
import { useAuth } from "../hooks/useAuth";
import { api } from "../api/client";
import { useQueryClient } from "@tanstack/react-query";
import { Ic } from "./Icons";
import { ICONS } from "./iconsData";
import { HelpModal } from "./Modals/HelpModal";
import { checkPasswordStrength } from "../utils/passwordPolicy";

const LOGIN_PLACEHOLDER = "ivan.konstruktorov";

const validateLoginInput = (value, allowedDomains) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return "Логин обязателен";
  if (!trimmed.includes("@")) return null;

  const lower = trimmed.toLowerCase();
  if (lower.startsWith("@") || lower.endsWith(".") || lower.includes(".."))
    return "Некорректный формат e-mail";
  if (
    allowedDomains.length > 0 &&
    !allowedDomains.some((d) => lower.endsWith(d))
  ) {
    return "Допускаются домены " + allowedDomains.join(", ");
  }
  return null;
};

export default function LoginScreen({ toast, forcePasswordChange = false }) {
  const { login } = useAuth();
  const queryClient = useQueryClient();
  const allowedDomains = useAllowedEmailDomains();

  const flashTimerRef = useRef(null);
  useEffect(
    () => () => {
      clearTimeout(flashTimerRef.current);
    },
    [],
  );

  const [mode, setMode] = useState(
    forcePasswordChange ? "change-password" : "login",
  );

  const [prevForcePasswordChange, setPrevForcePasswordChange] =
    useState(forcePasswordChange);
  if (forcePasswordChange !== prevForcePasswordChange) {
    setPrevForcePasswordChange(forcePasswordChange);
    if (forcePasswordChange) {
      setMode("change-password");
    }
  }

  const [lg, setLg] = useState("");
  const [pw, setPw] = useState("");
  const { shown: showPassword, toggle: togglePasswordVisibility } =
    usePasswordReveal();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [shake, setShake] = useState(false);

  const [loginEmailError, setLoginEmailError] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);

  const [chg, setChg] = useState({ old: "", new: "", confirm: "" });
  const [setPasswordToken, setSetPasswordToken] = useState(null);

  const { issues, isValid: isPasswordValid } = checkPasswordStrength(chg.new);

  const hideOldPassword = forcePasswordChange || !!setPasswordToken;

  const flash = () => {
    setShake(true);
    clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => {
      flashTimerRef.current = null;
      setShake(false);
    }, 450);
  };

  const rejectField = () => {
    flash();
  };

  const reject = (message) => {
    setErr(message);
    flash();
  };

  const doLogin = async (identifier, pass) => {
    const loginErr = validateLoginInput(identifier, allowedDomains);
    if (loginErr) {
      setLoginEmailError(loginErr);
      rejectField();
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const data = await login(identifier, pass);
      if (data && data.setPasswordToken) {
        setSetPasswordToken(data.setPasswordToken);
        setMode("change-password");
      }
      if (data && data.mustChangePassword) {
        setMode("change-password");
      }
    } catch (e) {
      reject(e.message || "Ошибка входа");
    } finally {
      setBusy(false);
    }
  };

  const handleLoginEmailChange = (value) => {
    setLg(value);
    setErr(null);
    if (loginEmailError) setLoginEmailError(null);
  };

  const handleLoginEmailBlur = () => {
    if (!lg.trim()) {
      setLoginEmailError(null);
      return;
    }
    setLoginEmailError(validateLoginInput(lg, allowedDomains));
  };

  const submitPasswordChange = async () => {
    if (!chg.new) {
      reject("Заполните новый пароль");
      return;
    }

    const currentPasswordToCheck = hideOldPassword ? "" : chg.old;

    if (!hideOldPassword && !currentPasswordToCheck) {
      reject("Заполните текущий пароль");
      return;
    }
    if (chg.new !== chg.confirm) {
      reject("Пароли не совпадают");
      return;
    }
    if (!isPasswordValid) {
      reject("Пароль не соответствует требованиям безопасности");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      if (setPasswordToken) {
        await api.post("/auth/set-password", {
          token: setPasswordToken,
          newPassword: chg.new,
          confirmPassword: chg.confirm,
        });
        setChg({ old: "", new: "", confirm: "" });
        setSetPasswordToken(null);
        setPw("");
        setMode("login");
        if (toast)
          toast("Пароль установлен. Войдите с новым паролем.", "success");
      } else {
        await api.post("/auth/change-password", {
          oldPassword: currentPasswordToCheck,
          newPassword: chg.new,
        });
        setChg({ old: "", new: "", confirm: "" });
        await queryClient.invalidateQueries({ queryKey: ["me"] });
      }
    } catch (e) {
      reject(e.message || "Не удалось сохранить пароль");
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (mode === "change-password") {
      await submitPasswordChange();
      return;
    }

    try {
      if (mode === "login") {
        await doLogin(lg, pw);
      }
    } catch (ex) {
      reject("Внутренняя ошибка: " + (ex && ex.message ? ex.message : ex));
    }
  };

  return (
    <div className="login-wrap">
      <button
        type="button"
        className="btn primary big login-help-fab"
        onClick={() => setHelpOpen(true)}
        title="Открыть справку"
        aria-label="Открыть справку"
      >
        <Ic d={ICONS.help} size={18} />
        <span>Справка</span>
      </button>

      <div className="login-hero">
        <div className="logo lg">
          <div className="logo-mark">АГ</div>
          <div>
            <div className="logo-name">АвиаГоризонт</div>
            <div className="logo-sub">планирование и учёт времени</div>
          </div>
        </div>
        <h2>Единая среда планирования ИЦ</h2>
        <p>
          АвиаГоризонт - корпоративная система планирования и учёта рабочего
          времени инженерного центра. Канбан, список, диаграмма Ганта и
          календарь - четыре проекции одной базы задач. Производственные проекты
          и административные проекты предприятия, задачи с подзадачами любой
          глубины и зависимостями, учёт часов, шаблоны, отчёты, архив и журнал
          аудита.
        </p>
        <ul className="hero-list">
          <li>
            <b>10 ролей</b>, делегирование полномочий, отпуска с
            автопереназначением задач на замещающего
          </li>
          <li>
            <b>Задачи с подзадачами</b> любой глубины, 4 типа зависимостей,
            автосдвиг дат последователей, распределение загрузки по узлам дерева
          </li>
          <li>
            <b>Учёт времени и загрузка:</b> часы по дням, контроль перерасхода,
            диаграмма загрузки с учётом производственного календаря РФ
          </li>
          <li>
            <b>Шаблоны задач и проектов</b> с деревом подзадач, личные и общие
          </li>
          <li>
            <b>Обсуждения и файлы:</b> ветки ответов, @упоминания, реакции,
            папки, версии документов, публичные ссылки
          </li>
          <li>
            <b>Запросы и заявки:</b> изменение часов и сроков, согласование
            отпусков, делегирование ролей, регистрация сотрудников
          </li>
          <li>
            <b>Отчёты, архив, журнал аудита</b> с фильтрами и выгрузкой в CSV
          </li>
          <li>
            <b>Диаграмма Ганта</b> - временная шкала задач с зависимостями
          </li>
        </ul>
        <div className="hero-stack">
          React · Vite · Node.js · PostgreSQL · Ubuntu LTS · ООП/KISS/DRY
        </div>
      </div>

      <div className="login-panel">
        <form
          className={"login-card" + (shake ? " shake" : "")}
          onSubmit={submit}
        >
          {mode !== "change-password" && (
            <>
              <h3>Вход в систему</h3>
              <div className="login-sub">Введите логин или рабочий e-mail</div>

              <div className="info-box" role="note">
                <b>Логин</b> - часть e-mail до знака <code>@</code>. Например,
                для <code>ivan.konstruktorov@hor.ru</code> логин -{" "}
                <code>ivan.konstruktorov</code>. Полный e-mail тоже принимается.
              </div>

              <label className="lbl" htmlFor="login-input">
                Логин
              </label>
              <input
                id="login-input"
                className="inp"
                type="text"
                autoComplete="username"
                name="login"
                value={lg}
                onChange={(e) => handleLoginEmailChange(e.target.value)}
                onBlur={handleLoginEmailBlur}
                placeholder={LOGIN_PLACEHOLDER}
                autoFocus
              />
              {loginEmailError && (
                <div className="login-err">{loginEmailError}</div>
              )}

              <label className="lbl">Пароль</label>
              <div className="relative">
                <input
                  className="inp"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={pw}
                  onChange={(e) => {
                    setPw(e.target.value);
                    setErr(null);
                  }}
                  placeholder="с учётом регистра"
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="pass-toggle-btn"
                >
                  <Ic d={ICONS.eye} size={18} />
                </button>
              </div>
            </>
          )}

          {mode === "change-password" && (
            <>
              <h3>{setPasswordToken ? "Установка пароля" : "Смена пароля"}</h3>
              <div className="login-sub">
                {setPasswordToken
                  ? "Установите пароль для входа в систему. После сохранения войдите с новым паролем."
                  : "Установленный вам пароль нужно заменить перед началом работы."}
              </div>

              {!hideOldPassword && (
                <>
                  <label className="lbl">Текущий пароль</label>
                  <input
                    className="inp"
                    type="password"
                    autoComplete="current-password"
                    value={chg.old}
                    onChange={(e) => setChg({ ...chg, old: e.target.value })}
                  />
                </>
              )}

              <label className="lbl">Новый пароль</label>
              <input
                className="inp"
                type="password"
                autoComplete="new-password"
                value={chg.new}
                onChange={(e) => setChg({ ...chg, new: e.target.value })}
              />

              <div className="pass-checks">
                {issues.map((i) => (
                  <span key={i.t} className={i.ok ? "ok" : ""}>
                    ✓ {i.t}
                  </span>
                ))}
              </div>

              <label className="lbl">Повторите новый пароль</label>
              <input
                className="inp"
                type="password"
                autoComplete="new-password"
                value={chg.confirm}
                onChange={(e) => setChg({ ...chg, confirm: e.target.value })}
              />

              <button
                className="btn primary big"
                type="submit"
                disabled={busy || !isPasswordValid}
              >
                {busy
                  ? "Сохранение…"
                  : setPasswordToken
                    ? "Установить пароль"
                    : "Сменить пароль"}
              </button>
            </>
          )}

          {err && <div className="login-err">{err}</div>}

          {mode !== "change-password" && (
            <button className="btn primary big" type="submit" disabled={busy}>
              {busy ? "Выполняется вход…" : "Войти"}
            </button>
          )}
        </form>
      </div>

      {helpOpen && <HelpModal onClose={() => setHelpOpen(false)} />}
    </div>
  );
}
